import {
  getFirestore, collection, doc, setDoc, addDoc, updateDoc,
  deleteDoc, onSnapshot, query, where, orderBy, getDoc, getDocs, Timestamp,
  runTransaction
} from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { checkChange } from '../ledger/effects';
import { DEBT_ROUNDING_TOLERANCE, validateLoanCorrection, cardUsedAfterLoanPayment, cardUsedAfterLoanReversal } from '../finance/debt';

// Get Firestore instance
const getDb = () => {
  return getFirestore();
};

// Helper: Get debt collection path
const getDebtPath = (userId) => {
  return `artifacts/${APP_ID}/users/${userId}/debts`;
};

// Helper: Get payment collection path
const getPaymentPath = (userId, debtId) => {
  return `artifacts/${APP_ID}/users/${userId}/debts/${debtId}/payments`;
};

/**
 * Add new debt
 * @param {string} userId 
 * @param {Object} debtData - {lender, principal, interestRate, term, dueDate, type, note}
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export const addDebt = async (userId, debtData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!debtData.lender) throw new Error('Lender name is required');
    if (!debtData.principal || debtData.principal <= 0) throw new Error('Principal amount must be greater than 0');

    const debtRef = collection(getDb(), getDebtPath(userId));
    
    const newDebt = {
      lender: debtData.lender.trim(),
      principal: Number(debtData.principal),
      remainingBalance: Number(debtData.principal), // Initially equal to principal
      interestRate: Number(debtData.interestRate) || 0,
      term: Number(debtData.term) || 0, // months
      dueDate: debtData.dueDate ? Timestamp.fromDate(new Date(debtData.dueDate)) : null,
      type: debtData.type || 'personal', // personal, bank, credit-card, other
      status: 'active', // active, paid, overdue
      note: debtData.note?.trim() || '',
      // Credit card debt fields
      creditCardId: debtData.creditCardId || null,
      // Personal/bank/other debt fields
      sourceAccountId: debtData.sourceAccountId || null, // Tài khoản nhận tiền vay
      actualReceived: debtData.actualReceived ? Number(debtData.actualReceived) : null, // Số tiền thực nhận
      // Phương thức trả: 'annuity' (trả góp đều gốc+lãi — chuẩn ngân hàng) | 'flexible'
      repaymentMethod: debtData.repaymentMethod || 'flexible',
      monthlyPayment: debtData.monthlyPayment ? Number(debtData.monthlyPayment) : null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(debtRef, newDebt);
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding debt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * GA2 — Lấy TOÀN BỘ lịch sử trả nợ của mọi khoản nợ (cho khung "Tiền ra khỏi ví").
 * Vòng lặp per-debt chấp nhận được: số khoản nợ cá nhân là vài đơn vị (PB5).
 * Chỉ gọi lazy khi người dùng mở khung cash — không tải sẵn.
 */
export const getAllDebtPayments = async (userId) => {
  try {
    const db = getDb();
    const debtsSnap = await getDocs(collection(db, getDebtPath(userId)));
    const all = [];
    for (const debtDoc of debtsSnap.docs) {
      const debt = debtDoc.data();
      const paysSnap = await getDocs(collection(db, getPaymentPath(userId, debtDoc.id)));
      paysSnap.docs.forEach(p => {
        const d = p.data();
        all.push({
          id: p.id,
          debtId: debtDoc.id,
          lender: debt.lender || '',
          totalAmount: d.totalAmount || 0,
          principal: d.principal || 0,
          interest: d.interest || 0,
          paymentDate: d.paymentDate?.toDate?.() || null,
          sourceId: d.sourceId || null
        });
      });
    }
    return { success: true, data: all };
  } catch (error) {
    console.error('Error getting all debt payments:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Đảo chiều (xoá) MỘT lần trả nợ ghi nhầm — Đ4, quyết định 17/07/2026.
 *
 * Chỉ cho đảo bản ghi MỚI NHẤT (đúng nguyên tắc kế toán: đảo theo thứ tự,
 * không rút giữa chuỗi làm sai các kỳ sau). Transaction nguyên tử:
 * hoàn ví (nếu có nguồn), tăng lại dư nợ theo GỐC, xoá khoản chi lãi tự sinh
 * (truy qua relatedPaymentId), và ghi vết audit 'payment-reversal' —
 * xoá không phải là biến mất, mà là một bút toán đảo có dấu vết.
 */
export const reverseLatestPayment = async (userId, debtId, paymentId) => {
  try {
    if (!userId || !debtId || !paymentId) throw new Error('Thiếu thông tin');

    const db = getDb();

    // Xác minh đây là bản ghi mới nhất (ngoài transaction — đọc danh sách)
    const paymentsSnap = await getDocs(
      query(collection(db, getPaymentPath(userId, debtId)), orderBy('paymentDate', 'desc'))
    );
    if (paymentsSnap.empty) throw new Error('Không có lịch sử trả nợ');
    if (paymentsSnap.docs[0].id !== paymentId) {
      throw new Error('Chỉ đảo được lần trả MỚI NHẤT — đảo theo thứ tự để không làm sai các kỳ sau');
    }

    // Tìm khoản chi lãi tự sinh gắn với payment này (nếu có)
    const expensesRef = collection(db, `artifacts/${APP_ID}/users/${userId}/expenses`);
    const linkedExpenseSnap = await getDocs(
      query(expensesRef, where('relatedPaymentId', '==', paymentId))
    );
    const linkedExpenseIds = linkedExpenseSnap.docs.map(d => d.id);

    await runTransaction(db, async (transaction) => {
      const paymentRef = doc(db, getPaymentPath(userId, debtId), paymentId);
      const debtRef = doc(db, getDebtPath(userId), debtId);

      const [paymentSnap, debtSnap] = await Promise.all([
        transaction.get(paymentRef),
        transaction.get(debtRef)
      ]);
      if (!paymentSnap.exists()) throw new Error('Bản ghi trả nợ không còn tồn tại');
      if (!debtSnap.exists()) throw new Error('Khoản nợ không tồn tại');

      const payment = paymentSnap.data();
      const debt = debtSnap.data();
      let linkedCard = null;
      if (debt.creditCardId) {
        const cardRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${debt.creditCardId}`);
        const cardSnap = await transaction.get(cardRef);
        if (cardSnap.exists()) linkedCard = { ref: cardRef, data: cardSnap.data() };
      }

      // Hoàn ví nếu lần trả có nguồn tiền
      if (payment.sourceId) {
        const sourceRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${payment.sourceId}`);
        const sourceSnap = await transaction.get(sourceRef);
        if (sourceSnap.exists()) {
          transaction.update(sourceRef, {
            balance: (sourceSnap.data().balance || 0) + (payment.totalAmount || 0),
            updatedAt: Timestamp.now()
          });
        }
      }

      // Tăng lại dư nợ theo phần GỐC, không vượt tiền gốc ban đầu
      const restored = Math.min(
        (debt.remainingBalance || 0) + (payment.principal || 0),
        debt.principal || Infinity
      );
      transaction.update(debtRef, {
        remainingBalance: restored,
        status: restored > 0 ? 'active' : debt.status,
        updatedAt: Timestamp.now()
      });

      if (linkedCard && payment.principal > 0) {
        transaction.update(linkedCard.ref, { usedCredit: cardUsedAfterLoanReversal(linkedCard.data.usedCredit, payment.principal, linkedCard.data.creditLimit), updatedAt: Timestamp.now() });
      }

      // Xoá bản ghi trả nợ + khoản chi lãi tự sinh liên kết
      transaction.delete(paymentRef);
      linkedExpenseIds.forEach(id => {
        transaction.delete(doc(db, `artifacts/${APP_ID}/users/${userId}/expenses/${id}`));
      });

      // Vết audit — bút toán đảo, ai cũng thấy trong lịch sử giao dịch
      const auditRef = doc(collection(db, `artifacts/${APP_ID}/users/${userId}/transactions`));
      transaction.set(auditRef, {
        debtId,
        sourceId: payment.sourceId || null,
        amount: payment.totalAmount || 0,
        type: 'payment-reversal',
        description: `Đảo lần trả nợ ${debt.lender || ''}: hoàn ${(payment.totalAmount || 0).toLocaleString('vi-VN')} đ (gốc ${(payment.principal || 0).toLocaleString('vi-VN')} + lãi ${(payment.interest || 0).toLocaleString('vi-VN')})`,
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now()
      });
    });

    return { success: true, message: 'Đã đảo lần trả nợ (có ghi vết)' };
  } catch (error) {
    console.error('Error reversing payment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Đối soát dư nợ về đúng số của ngân hàng (reconciliation).
 *
 * Đường DUY NHẤT hợp lệ để sửa tay remainingBalance — transaction nguyên tử,
 * luôn ghi vết audit (số cũ → số mới, lý do bắt buộc) vào collection
 * 'transactions' chung, cùng chuẩn với adjustBalance của nguồn tiền.
 * Dùng khi số trong app lệch với bảng sao kê/amortization của ngân hàng.
 *
 * @param {string} userId
 * @param {string} debtId
 * @param {number} actualRemaining - Dư nợ thực tế theo ngân hàng
 * @param {string} reason - Lý do (bắt buộc), vd: "đối soát bảng Shinhan kỳ 6"
 */
export const adjustRemainingBalance = async (userId, debtId, actualRemaining, reason) => {
  try {
    if (!reason || !reason.trim()) {
      return { success: false, error: 'Cần nhập lý do đối soát' };
    }
    if (typeof actualRemaining !== 'number' || Number.isNaN(actualRemaining) || actualRemaining < 0) {
      return { success: false, error: 'Dư nợ không hợp lệ' };
    }

    const db = getDb();
    const debtRef = doc(db, getDebtPath(userId), debtId);

    let auditInfo = null;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(debtRef);
      if (!snap.exists()) throw new Error('Không tìm thấy khoản nợ');

      const data = snap.data();
      const oldValue = Number(data.remainingBalance) || 0;
      if (actualRemaining > (Number(data.principal) || 0)) {
        throw new Error('Dư nợ không thể lớn hơn tiền gốc ban đầu');
      }

      transaction.update(debtRef, {
        remainingBalance: actualRemaining,
        status: actualRemaining === 0 ? 'paid' : 'active',
        updatedAt: Timestamp.now()
      });

      const auditRef = doc(collection(db, `artifacts/${APP_ID}/users/${userId}/transactions`));
      transaction.set(auditRef, {
        debtId,
        amount: oldValue - actualRemaining, // quy về tác động lên tài sản ròng
        type: 'debt-adjustment',
        oldValue,
        newValue: actualRemaining,
        reason: reason.trim(),
        description: `Đối soát dư nợ ${data.lender || ''}: ${oldValue.toLocaleString('vi-VN')} → ${actualRemaining.toLocaleString('vi-VN')} (${reason.trim()})`,
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now()
      });

      auditInfo = { oldValue, newValue: actualRemaining, difference: actualRemaining - oldValue };
    });

    return { success: true, data: auditInfo };
  } catch (error) {
    console.error('Error adjusting remaining balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sửa thông tin khoản vay khi nhập nhầm (tên, tiền vay gốc, dư nợ còn lại, hạn, ghi chú) trong MỘT transaction,
 * kèm dòng lịch sử. Không đụng tới các lần đã trả. Số dư theo ngân hàng thì dùng đối soát dư nợ.
 */
export const correctDebt = async (userId, debtId, fix) => {
  try {
    const lender = String(fix.lender ?? '').trim();
    const principal = Number(fix.principal), remaining = Number(fix.remainingBalance);
    const rate = fix.interestRate === undefined || fix.interestRate === '' ? undefined : Number(fix.interestRate);
    const term = fix.term === undefined || fix.term === '' ? undefined : Number(fix.term);
    const monthly = fix.monthlyPayment === undefined ? undefined : fix.monthlyPayment === null || fix.monthlyPayment === '' ? null : Number(fix.monthlyPayment);
    const bad = validateLoanCorrection({ lender, principal, remainingBalance: remaining, interestRate: rate, term, monthlyPayment: monthly });
    if (bad) return { success: false, error: bad };
    const db = getDb();
    const ref = doc(db, getDebtPath(userId), debtId);
    let info = null;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Không tìm thấy khoản nợ');
      const old = snap.data();
      const oldRemaining = Number(old.remainingBalance) || 0;
      const now = Timestamp.now();
      const patch = {
        lender, principal, remainingBalance: remaining,
        status: remaining === 0 ? 'paid' : old.status === 'paid' ? 'active' : old.status,
        note: String(fix.note ?? ''), updatedAt: now,
        creditCardId: fix.creditCardId || null,
        ...(rate !== undefined ? { interestRate: rate } : {}),
        ...(term !== undefined ? { term } : {}),
        ...(monthly !== undefined ? { monthlyPayment: monthly } : {}),
        ...(fix.repaymentMethod ? { repaymentMethod: fix.repaymentMethod } : {}),
        dueDate: fix.dueDate ? Timestamp.fromDate(new Date(fix.dueDate)) : null
      };
      tx.update(ref, patch);
      tx.set(doc(collection(db, `artifacts/${APP_ID}/users/${userId}/transactions`)), {
        debtId, amount: oldRemaining - remaining, type: 'debt-adjustment', oldValue: oldRemaining, newValue: remaining,
        reason: 'Sửa thông tin khoản vay',
        description: `Sửa khoản vay ${lender}: gốc ${(Number(old.principal) || 0).toLocaleString('vi-VN')} → ${principal.toLocaleString('vi-VN')}, dư nợ ${oldRemaining.toLocaleString('vi-VN')} → ${remaining.toLocaleString('vi-VN')}${rate !== undefined && rate !== Number(old.interestRate) ? `, lãi ${Number(old.interestRate) || 0}% → ${rate}%` : ''}`,
        timestamp: now, createdAt: now
      });
      info = { oldRemaining, newRemaining: remaining };
    });
    return { success: true, data: info };
  } catch (error) {
    console.error('Error correcting debt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update existing debt
 * @param {string} userId
 * @param {string} debtId
 * @param {Object} updates
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateDebt = async (userId, debtId, updates) => {
  try {
    if (!userId || !debtId) throw new Error('User ID and Debt ID are required');

    const debtRef = doc(getDb(), getDebtPath(userId), debtId);
    
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    // Convert date if provided
    if (updates.dueDate) {
      updateData.dueDate = Timestamp.fromDate(new Date(updates.dueDate));
    }

    // Ensure numeric fields
    if (updates.principal !== undefined) updateData.principal = Number(updates.principal);
    if (updates.interestRate !== undefined) updateData.interestRate = Number(updates.interestRate);
    if (updates.term !== undefined) updateData.term = Number(updates.term);
    if (updates.remainingBalance !== undefined) updateData.remainingBalance = Number(updates.remainingBalance);

    await updateDoc(debtRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating debt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete debt
 * @param {string} userId 
 * @param {string} debtId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteDebt = async (userId, debtId) => {
  try {
    if (!userId || !debtId) throw new Error('User ID and Debt ID are required');

    const debtRef = doc(getDb(), getDebtPath(userId), debtId);
    await deleteDoc(debtRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting debt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add payment to debt
 * Integrated with source balance update via Firestore transaction
 * @param {string} userId 
 * @param {string} debtId 
 * @param {Object} paymentData - {totalAmount, principal, interest, paymentDate, note, sourceId}
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addPayment = async (userId, debtId, paymentData) => {
  try {
    if (!userId || !debtId) throw new Error('User ID and Debt ID are required');
    if (!paymentData.totalAmount || paymentData.totalAmount <= 0) throw new Error('Payment amount must be greater than 0');
    // Gốc hoặc lãi được phép = 0 (vay lãi 0%, kỳ chỉ trả lãi) — bản cũ dùng `!x` nên từ chối cả số 0
    if (!Number.isFinite(Number(paymentData.principal)) || Number(paymentData.principal) < 0) throw new Error('Principal amount cannot be negative');
    if (!Number.isFinite(Number(paymentData.interest)) || Number(paymentData.interest) < 0) throw new Error('Interest amount cannot be negative');

    const totalAmount = Number(paymentData.totalAmount);
    let principalAmount = Number(paymentData.principal);
    let interestAmount = Number(paymentData.interest);

    if (principalAmount + interestAmount !== totalAmount) {
      throw new Error('Principal + Interest must equal Total Amount');
    }

    const db = getDb();

    // Use transaction to ensure atomicity
    await runTransaction(db, async (transaction) => {
      // 1. Get current debt
      const debtRef = doc(db, getDebtPath(userId), debtId);
      const debtSnap = await transaction.get(debtRef);
      
      if (!debtSnap.exists()) throw new Error('Debt not found');
      
      const currentDebt = debtSnap.data();
      
      // Kỳ cuối lệch vài đồng do làm tròn: chốt dư nợ về 0, phần dư tính vào lãi
      if (principalAmount > currentDebt.remainingBalance && principalAmount - currentDebt.remainingBalance <= DEBT_ROUNDING_TOLERANCE) {
        interestAmount += principalAmount - currentDebt.remainingBalance;
        principalAmount = currentDebt.remainingBalance;
      }
      if (principalAmount > currentDebt.remainingBalance) {
        throw new Error(`Principal amount (${principalAmount.toLocaleString()}) exceeds remaining balance (${currentDebt.remainingBalance.toLocaleString()})`);
      }

      const newRemainingBalance = Math.max(0, currentDebt.remainingBalance - principalAmount);

      // Khoản vay gắn thẻ tín dụng: phần gốc trả đi cũng làm giảm dư nợ thẻ (đọc trước mọi lệnh ghi)
      let linkedCard = null;
      if (currentDebt.creditCardId) {
        const cardRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${currentDebt.creditCardId}`);
        const cardSnap = await transaction.get(cardRef);
        if (cardSnap.exists()) linkedCard = { ref: cardRef, data: cardSnap.data() };
      }

      // 2. If sourceId provided, update source balance
      if (paymentData.sourceId) {
        const sourceRef = doc(db, `artifacts/${APP_ID}/users/${userId}/sources/${paymentData.sourceId}`);
        const sourceSnap = await transaction.get(sourceRef);
        
        if (!sourceSnap.exists()) {
          throw new Error('Payment source not found');
        }

        const sourceData = sourceSnap.data();
        
        // Check if source is credit card (should not be used for debt payment)
        if (sourceData.type === 'credit') {
          throw new Error('Cannot pay debt using credit card. Please use CreditPaymentManager for credit card payments.');
        }

        const currentBalance = sourceData.balance || 0;
        
        const policy = checkChange(sourceData, 'balance', -totalAmount);
        if (!policy.ok) throw new Error(policy.error);

        const newBalance = currentBalance - totalAmount;
        
        // Update source balance
        transaction.update(sourceRef, {
          balance: newBalance,
          updatedAt: Timestamp.now()
        });
      }

      // 3. Add payment record
      const paymentRef = collection(db, getPaymentPath(userId, debtId));
      const newPaymentRef = doc(paymentRef);
      
      const payment = {
        totalAmount: totalAmount,
        principal: principalAmount,
        interest: interestAmount,
        paymentDate: paymentData.paymentDate ? Timestamp.fromDate(new Date(paymentData.paymentDate)) : Timestamp.now(),
        note: paymentData.note?.trim() || '',
        sourceId: paymentData.sourceId || null,
        createdAt: Timestamp.now()
      };

      transaction.set(newPaymentRef, payment);

      // 4. Update debt remaining balance and status
      const updates = {
        remainingBalance: newRemainingBalance,
        status: newRemainingBalance === 0 ? 'paid' : 'active',
        updatedAt: Timestamp.now()
      };

      transaction.update(debtRef, updates);
      if (linkedCard && principalAmount > 0) {
        transaction.update(linkedCard.ref, { usedCredit: cardUsedAfterLoanPayment(linkedCard.data.usedCredit, principalAmount), updatedAt: Timestamp.now() });
      }

      // 5. Lãi vay = chi phí tài chính: tự sinh bản ghi chi tiêu để báo cáo
      // phản ánh đúng giá của việc vay. KHÔNG trừ ví lần nữa (totalAmount đã
      // gồm lãi và đã trừ ở bước 2) — bản ghi kế toán thuần, gắn autoGenerated.
      // Nếu bạn đang tự ghi tay khoản chi lãi vay, hãy ngừng để tránh trùng.
      if (interestAmount > 0) {
        const expensesRef = collection(db, `artifacts/${APP_ID}/users/${userId}/expenses`);
        const payDate = paymentData.paymentDate ? new Date(paymentData.paymentDate) : new Date();
        transaction.set(doc(expensesRef), {
          amount: interestAmount,
          category: 'Chi phí tài chính',
          categoryName: 'Chi phí tài chính',
          description: `Lãi vay ${currentDebt.lender || ''} (tự động từ trả nợ)`,
          transactionDate: Timestamp.fromDate(payDate),
          month: payDate.getMonth() + 1,
          year: payDate.getFullYear(),
          autoGenerated: true,
          relatedDebtId: debtId,
          relatedPaymentId: newPaymentRef.id,
          createdAt: Timestamp.now()
        });
      }
    });
    
    return { success: true, message: 'Payment added successfully' };
  } catch (error) {
    console.error('Error adding payment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all debts for user (one-time fetch)
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getDebts = async (userId) => {
  try {
    const debtsRef = collection(getDb(), getDebtPath(userId));
    const q = query(debtsRef, orderBy('createdAt', 'desc'));
    
    const snapshot = await getDocs(q);
    const debts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, data: debts };
  } catch (error) {
    console.error('Error getting debts:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get single debt by ID
 * @param {string} userId 
 * @param {string} debtId 
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getDebt = async (userId, debtId) => {
  try {
    if (!userId || !debtId) throw new Error('User ID and Debt ID are required');
    
    const debtRef = doc(getDb(), getDebtPath(userId), debtId);
    const debtSnap = await getDoc(debtRef);
    
    if (!debtSnap.exists()) {
      return { success: false, error: 'Debt not found' };
    }
    
    const debtData = debtSnap.data();
    return { 
      success: true, 
      data: {
        id: debtSnap.id,
        ...debtData,
        dueDate: debtData.dueDate?.toDate(),
        createdAt: debtData.createdAt?.toDate(),
        updatedAt: debtData.updatedAt?.toDate()
      }
    };
  } catch (error) {
    console.error('Error getting debt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all debts for user (real-time subscription)
 * @param {string} userId 
 * @param {Function} callback - (result) => void
 * @returns {Function} Unsubscribe function
 */
export const subscribeToDebts = (userId, callback) => {
  if (!userId) {
    callback({ success: false, error: 'User ID is required' });
    return () => {};
  }

  try {
    const debtsQuery = query(
      collection(getDb(), getDebtPath(userId)),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      debtsQuery,
      (snapshot) => {
        const debts = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          debts.push({
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          });
        });
        
        callback({ success: true, data: debts });
      },
      (error) => {
        console.error('Error subscribing to debts:', error);
        callback({ success: false, error: error.message });
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up debt subscription:', error);
    callback({ success: false, error: error.message });
    return () => {};
  }
};

/**
 * Get payment history for a debt (real-time subscription)
 * @param {string} userId 
 * @param {string} debtId 
 * @param {Function} callback 
 * @returns {Function} Unsubscribe function
 */
export const subscribeToPayments = (userId, debtId, callback) => {
  if (!userId || !debtId) {
    callback({ success: false, error: 'User ID and Debt ID are required' });
    return () => {};
  }

  try {
    // Lịch sử trả nợ nằm ở HAI sổ do lỗi cũ:
    // - 'payments': sổ chuẩn (tab Nợ + xác nhận dự thanh toán từ 17/07/2026)
    // - 'repayments': sổ di sản do xác nhận dự thanh toán ghi TRƯỚC khi hợp nhất
    // Gộp ở tầng đọc (không sửa dữ liệu gốc): bản ghi di sản được chuẩn hoá
    // về cùng schema — toàn bộ tiền là gốc, lãi 0 (đúng cách nó từng được
    // trừ vào dư nợ), gắn cờ legacy để phân biệt khi cần.
    const merged = { payments: [], repayments: [] };

    const emit = () => {
      const all = [...merged.payments, ...merged.repayments]
        .sort((a, b) => (b.paymentDate?.getTime?.() || 0) - (a.paymentDate?.getTime?.() || 0));
      callback({ success: true, data: all });
    };

    const paymentsQuery = query(
      collection(getDb(), getPaymentPath(userId, debtId)),
      orderBy('paymentDate', 'desc')
    );
    const unsubPayments = onSnapshot(
      paymentsQuery,
      (snapshot) => {
        merged.payments = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate(),
            createdAt: data.createdAt?.toDate()
          };
        });
        emit();
      },
      (error) => {
        console.error('Error subscribing to payments:', error);
        callback({ success: false, error: error.message });
      }
    );

    const repaymentsRef = collection(
      getDb(), `artifacts/${APP_ID}/users/${userId}/debts/${debtId}/repayments`
    );
    const unsubRepayments = onSnapshot(
      repaymentsRef,
      (snapshot) => {
        merged.repayments = snapshot.docs.map((doc) => {
          const data = doc.data();
          const amount = Number(data.amount) || 0;
          return {
            id: doc.id,
            totalAmount: amount,
            principal: amount, // sổ cũ trừ toàn bộ vào dư nợ → coi là gốc
            interest: 0,
            paymentDate: data.date?.toDate(),
            createdAt: data.createdAt?.toDate(),
            note: data.note || '',
            sourceId: data.sourceWalletId || null,
            legacy: true // từ sổ 'repayments' cũ (xác nhận dự thanh toán trước 17/07/2026)
          };
        });
        emit();
      },
      (error) => {
        // Sổ di sản có thể không tồn tại — không coi là lỗi chặn
        console.warn('Legacy repayments subscription:', error.message);
      }
    );

    return () => {
      unsubPayments();
      unsubRepayments();
    };
  } catch (error) {
    console.error('Error setting up payment subscription:', error);
    callback({ success: false, error: error.message });
    return () => {};
  }
};

/**
 * Get debt alerts (upcoming due dates)
 * @param {Array} debts 
 * @returns {Array} Alerts with severity levels
 */
export const getDebtAlerts = (debts) => {
  const alerts = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  debts.forEach(debt => {
    if (debt.status !== 'active' || !debt.dueDate) return;

    const dueDate = new Date(debt.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    let severity = null;
    let message = '';

    if (daysUntilDue < 0) {
      severity = 'critical';
      message = `Quá hạn ${Math.abs(daysUntilDue)} ngày`;
    } else if (daysUntilDue === 0) {
      severity = 'critical';
      message = 'Đến hạn hôm nay';
    } else if (daysUntilDue <= 3) {
      severity = 'high';
      message = `Còn ${daysUntilDue} ngày`;
    } else if (daysUntilDue <= 7) {
      severity = 'medium';
      message = `Còn ${daysUntilDue} ngày`;
    }

    if (severity) {
      alerts.push({
        debtId: debt.id,
        lender: debt.lender,
        remainingBalance: debt.remainingBalance,
        dueDate: debt.dueDate,
        daysUntilDue,
        severity,
        message
      });
    }
  });

  // Sort by severity (critical > high > medium)
  const severityOrder = { critical: 0, high: 1, medium: 2 };
  alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return alerts;
};

/**
 * Calculate debt statistics
 * @param {Array} debts 
 * @returns {Object} Statistics object
 */
export const calculateDebtStats = (allDebts) => {
  // Loại bản ghi nợ type='credit' di sản: dư nợ thẻ do usedCredit theo dõi
  // (sổ duy nhất) — cộng vào đây sẽ đếm trùng và lệch với khối Tổng Quan Nợ
  const debts = allDebts.filter(d => d.type !== 'credit');

  const stats = {
    totalDebts: debts.length,
    activeDebts: 0,
    paidDebts: 0,
    overdueDebts: 0,
    totalPrincipal: 0,
    totalRemaining: 0,
    totalPaid: 0,
    byType: {},
    byLender: {},
    avgInterestRate: 0,
    upcomingPayments: []
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let totalInterestRate = 0;
  let countWithInterest = 0;

  debts.forEach(debt => {
    // Status counts
    if (debt.status === 'active') stats.activeDebts++;
    if (debt.status === 'paid') stats.paidDebts++;
    
    if (debt.status === 'active' && debt.dueDate) {
      const dueDate = new Date(debt.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      if (dueDate < today) stats.overdueDebts++;
    }

    // Amounts
    stats.totalPrincipal += debt.principal || 0;
    stats.totalRemaining += debt.remainingBalance || 0;
    stats.totalPaid += (debt.principal || 0) - (debt.remainingBalance || 0);

    // By type
    const type = debt.type || 'other';
    if (!stats.byType[type]) {
      stats.byType[type] = { count: 0, remaining: 0 };
    }
    stats.byType[type].count++;
    stats.byType[type].remaining += debt.remainingBalance || 0;

    // By lender
    const lender = debt.lender || 'Unknown';
    if (!stats.byLender[lender]) {
      stats.byLender[lender] = { count: 0, remaining: 0 };
    }
    stats.byLender[lender].count++;
    stats.byLender[lender].remaining += debt.remainingBalance || 0;

    // Interest rate average
    if (debt.interestRate > 0) {
      totalInterestRate += debt.interestRate;
      countWithInterest++;
    }

    // Upcoming payments (within 30 days)
    if (debt.status === 'active' && debt.dueDate) {
      const dueDate = new Date(debt.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilDue >= 0 && daysUntilDue <= 30) {
        stats.upcomingPayments.push({
          debtId: debt.id,
          lender: debt.lender,
          amount: debt.remainingBalance,
          dueDate: debt.dueDate,
          daysUntilDue
        });
      }
    }
  });

  stats.avgInterestRate = countWithInterest > 0 ? totalInterestRate / countWithInterest : 0;
  stats.upcomingPayments.sort((a, b) => a.daysUntilDue - b.daysUntilDue);

  return stats;
};

/**
 * Get debt repayment progress
 * @param {Object} debt 
 * @returns {Object} Progress info
 */
export const getDebtProgress = (debt) => {
  const principal = debt.principal || 0;
  const remaining = debt.remainingBalance || 0;
  const paid = principal - remaining;
  const progress = principal > 0 ? (paid / principal) * 100 : 0;

  return {
    principal,
    remaining,
    paid,
    progress: Math.round(progress * 10) / 10, // Round to 1 decimal
    isFullyPaid: remaining === 0
  };
};
